import { Rgb, Scene, WallpaperStyleInfo, darken, fbm, lighten, luminance, mix, prng, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'synthwave', label: 'Synthwave',
  blurb: 'A retro sun over a glowing perspective grid, the 80s outrun look',
  params: [ share( 'sun', 'Sun', 55 ), share( 'grid', 'Grid', 50 ) ],
};

/**
 * Synthwave: a cut sun setting over a neon perspective grid, the 80s outrun poster. Everything is
 * vectors at full size (gradients, arcs, strokes), so there is no per-pixel pass at all; the neon
 * comes from stroking every line twice (a wide faint line under a thin bright one), never from
 * shadowBlur, which browsers draw differently and slowly.
 */
export function draw( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
  const s = box.w / 1920;
  const n = colors.length;
  // The tiles' order runs through the scene (owner, 2026-09-22): tile 1 is the sky, tile 2 the
  // mountains, tile 3 the top of the sun, tile 4 its bottom and the horizon, and the tiles after
  // that walk the floor from the horizon down to the bottom edge
  const ridgeLine = colors[ Math.min( 1, n - 1 ) ];
  const sunTop = n >= 3 ? colors[ 2 ] : ridgeLine;
  const sunBottom = n >= 4 ? colors[ 3 ] : lighten( sunTop, 0.5 );
  const lines = n >= 5 ? colors.slice( 4 ) : [ sunBottom ];
  /** the floor's colour at `t`, 0 at the horizon and 1 at the bottom edge: the tiles in order */
  const floorAt = ( t: number ): Rgb => {
    if ( lines.length < 2 ) return tube( lines[ 0 ] );
    const k = Math.max( 0, Math.min( lines.length - 1.0001, t * ( lines.length - 1 ) ) );
    const i = Math.floor( k );
    return tube( mix( lines[ i ], lines[ i + 1 ], k - i ) );
  };
  // the sky IS the first tile (owner, 2026-09-22): it is not pulled down to a night tone, only
  // deepened toward the top the way the other skies are, so a pale palette keeps its own sky
  const base = colors[ 0 ];
  const hy = box.y + box.h * 0.58;
  // a tile too dark to glow on the near black floor is lifted until it does, so a palette of deep
  // tones still reads as neon tubes rather than as scratches
  const tube = ( c: Rgb ) => {
    const l = luminance( c );
    return l < 0.36 ? lighten( c, 0.5 * ( 1 - l / 0.36 ) ) : c;
  };

  // a line stroked twice: the wide pass at 30% is the glow, the thin one the tube. `alpha` fades a
  // whole line out near the horizon; `style` swaps in a gradient for the lines that fade along themselves
  const neon = ( c: Rgb, width: number, trace: () => void, alpha = 1, style?: [ CanvasGradient, CanvasGradient ] ) => {
    if ( alpha <= 0.01 ) return;
    ctx.beginPath();
    trace();
    ctx.lineWidth = width * 3 * s;
    ctx.strokeStyle = style ? style[ 0 ] : rgba( c, 0.3 * alpha );
    ctx.stroke();
    ctx.lineWidth = width * s;
    ctx.strokeStyle = style ? style[ 1 ] : rgba( c, alpha );
    ctx.stroke();
  };

  // the sky: darkest at the top, the night tone at the horizon
  const sky = ctx.createLinearGradient( 0, box.y, 0, hy );
  sky.addColorStop( 0, rgba( darken( base, 0.3 ), 1 ) );
  sky.addColorStop( 1, rgba( base, 1 ) );
  ctx.fillStyle = sky;
  ctx.fillRect( 0, 0, w, h );

  // a faint field of stars above the horizon, from its own generator so the scene below draws the
  // same whatever the canvas size; they thin out toward the horizon where the sun's glow takes over
  const starRand = prng( seed ^ 0x5741 );
  // stars only show on a night sky; a pale one keeps them off rather than showing white specks
  const stars = luminance( base ) < 0.32 ? Math.round( 300 * ( w * h ) / ( box.w * box.h ) ) : 0;
  for ( let i = 0; i < stars; i++ ) {
    const x = starRand() * w;
    const v = starRand();
    const y = box.y + ( hy - box.y ) * v * 0.96;
    const r = ( 0.4 + starRand() * 1.2 ) * s;
    ctx.fillStyle = rgba( { r: 255, g: 255, b: 255 }, ( 0.2 + starRand() * 0.55 ) * ( 1 - v * 0.6 ) );
    ctx.beginPath();
    ctx.arc( x, y, Math.max( 0.5, r ), 0, Math.PI * 2 );
    ctx.fill();
  }

  // the sun: its size from the Sun field, its x seeded near the middle (a portrait crop keeps it),
  // sitting low enough that the horizon and the ridge cut its bottom off, the way it sets in the posters
  const r = box.h * ( 0.2 + p[ 0 ] * 0.14 );
  const cx = box.x + box.w * ( 0.5 + ( rand() - 0.5 ) * 0.10 );
  const cy = hy - r * ( 0.62 - p[ 0 ] * 0.2 );
  // its glow, added to the sky so it lights the stars and the horizon rather than covering them
  const glowR = r * ( 1.8 + p[ 0 ] * 1.0 );
  const glow = ctx.createRadialGradient( cx, cy, r * 0.6, cx, cy, glowR );
  // many stops on a smooth curve: three stops leave a kink at each one, which shows up as a faint
  // ring in the haze
  const peak = 0.3 + p[ 0 ] * 0.26;
  for ( let i = 0; i <= 12; i++ ) {
    const t = i / 12;
    glow.addColorStop( t, rgba( sunTop, peak * Math.pow( 1 - t, 3 ) ) );
  }
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = glow;
  ctx.fillRect( 0, 0, w, h );
  ctx.globalCompositeOperation = 'source-over';

  // the disc, filled top to bottom with tiles 2 and 3, and the classic slits through its lower half:
  // the disc is a clip and only the bands between the slits are filled, so the sky shows through
  // the cuts without touching what is already drawn. Few and wide slits (owner's stepping worry),
  // each taller than the one above, laid over the part that stays above the horizon
  ctx.save();
  ctx.beginPath();
  ctx.arc( cx, cy, r, 0, Math.PI * 2 );
  ctx.clip();
  const disc = ctx.createLinearGradient( 0, cy - r, 0, cy + r );
  disc.addColorStop( 0, rgba( sunTop, 1 ) );
  disc.addColorStop( 1, rgba( sunBottom, 1 ) );
  ctx.fillStyle = disc;
  const slits = 4 + Math.round( p[ 0 ] * 2 );
  // the cuts run from just above the middle of the disc down to the ridge crest: any lower and the
  // ridge hides most of them, and the sun stops reading as the cut one
  const cut0 = cy - r * 0.1;
  const cut1 = hy - box.h * 0.035;
  const pitch = ( cut1 - cut0 ) / slits;
  let top = cy - r;
  for ( let k = 0; k < slits; k++ ) {
    // each cell is a band over a slit; the slit grows from a fifth of the cell to two thirds of it
    const slit = pitch * ( 0.2 + 0.47 * k / Math.max( 1, slits - 1 ) );
    const bandBottom = cut0 + pitch * ( k + 1 ) - slit;
    ctx.fillRect( cx - r, top, r * 2, bandBottom - top );
    top = cut0 + pitch * ( k + 1 );
  }
  ctx.restore();

  // a low jagged ridge in front of the sun, across three box widths so a wider canvas continues
  // it; the noise is read at coarse steps and joined with straight lines, which is what makes it
  // read as mountains rather than hills
  const ridge = fbm( rand, 36, 3, 0.55 );
  const ridgeFill = darken( base, 0.58 );
  const steps = 150;
  const ridgeTop = ( i: number ) => {
    const u = i / steps;
    return { x: box.x + box.w * ( u * 3 - 1 ), y: hy - box.h * ( 0.012 + 0.065 * ridge( u, 0.5 ) ) };
  };
  ctx.beginPath();
  ctx.moveTo( box.x - box.w, hy + box.h * 0.05 );
  for ( let i = 0; i <= steps; i++ ) {
    const q = ridgeTop( i );
    ctx.lineTo( q.x, q.y );
  }
  ctx.lineTo( box.x + box.w * 2, hy + box.h * 0.05 );
  ctx.closePath();
  ctx.fillStyle = rgba( ridgeFill, 1 );
  ctx.fill();
  neon( tube( ridgeLine ), 1.6, () => {
    for ( let i = 0; i <= steps; i++ ) {
      const q = ridgeTop( i );
      if ( i === 0 ) ctx.moveTo( q.x, q.y ); else ctx.lineTo( q.x, q.y );
    }
  } );

  // the floor: the sky's own tone taken well down, so the grid has something to glow on whatever
  // the palette, then the lanes racing to the vanishing point under the sun
  ctx.fillStyle = rgba( darken( base, 0.72 ), 1 );
  ctx.fillRect( 0, hy, w, h - hy );
  ctx.save();
  ctx.beginPath();
  ctx.rect( 0, hy, w, h - hy );
  ctx.clip();
  ctx.lineCap = 'round';
  const bottom = box.y + box.h;
  const deep = box.h * 0.2;
  // every line dies out as it approaches the vanishing point: without it the lanes pile into a
  // solid wedge at the horizon and the near rows merge into one slab
  const haze = ( t: number ) => t * t * ( 3 - 2 * t );
  // one gradient for the whole floor: the colour walks the remaining tiles from the horizon down
  // to the bottom edge while the alpha fades the lane in out of the haze
  const floorStyle = (): [ CanvasGradient, CanvasGradient ] => {
    const g = ctx.createLinearGradient( 0, hy, 0, bottom );
    const k = ctx.createLinearGradient( 0, hy, 0, bottom );
    const stops = Math.max( 12, lines.length * 4 );
    const fade = ( t: number ) => haze( Math.min( 1, ( t * ( bottom - hy ) ) / deep ) );
    for ( let i = 0; i <= stops; i++ ) {
      const t = i / stops;
      const c = floorAt( t );
      g.addColorStop( t, rgba( c, 0.3 * fade( t ) ) );
      k.addColorStop( t, rgba( c, fade( t ) ) );
    }
    return [ g, k ];
  };
  // Grid: the lanes are evenly spaced along the bottom edge and all meet at the vanishing point.
  // The fan runs three box widths out to each side, because the lanes have to reach the far corners
  // of an ultrawide floor too, or the rows stand alone there and the sides read as stripes
  const lanes = 6 + Math.round( p[ 1 ] * 30 );
  const step = box.w / lanes;
  const reach = Math.ceil( 3 * box.w / step );
  const laneStyles = floorStyle();
  for ( let k = -reach; k <= reach; k++ ) {
    // the far lanes lie almost flat and would pile into a solid field with a hard edge along the
    // outermost one, so the fan dims away instead of stopping
    const d = Math.abs( k ) * step / box.w;
    const side = d <= 0.8 ? 1 : haze( Math.max( 0, 1 - ( d - 0.8 ) / 1.8 ) );
    if ( side <= 0.02 ) continue;
    ctx.globalAlpha = side;
    neon( lines[ 0 ], 2, () => {
      ctx.moveTo( cx, hy );
      ctx.lineTo( cx + k * step, bottom );
    }, 1, laneStyles );
  }
  ctx.globalAlpha = 1;
  // the rows bunch up toward the horizon; one that would land within a glow's width of the row
  // above is dropped, so the far floor stays separate lines instead of a band of mud
  const rows = 5 + Math.round( p[ 1 ] * 25 );
  const gap = 11 * s;
  let last = hy;
  for ( let k = 1; k <= rows; k++ ) {
    const y = hy + ( bottom - hy ) * Math.pow( k / ( rows + 0.5 ), 2.4 );
    if ( y - last < gap ) continue;
    last = y;
    // a row takes the floor's colour at its own depth, so the rows walk the tiles downward
    neon( floorAt( ( y - hy ) / ( bottom - hy ) ), 2, () => {
      ctx.moveTo( 0, y );
      ctx.lineTo( w, y );
    }, haze( Math.min( 1, ( y - hy ) / deep ) ) );
  }
  ctx.restore();

  // the horizon: a low band of light under one bright line in the sun's bottom tile, so the grid
  // seems to come out of a haze rather than to stop at a hard edge. The band is brightest at the
  // vanishing point and dies out to the sides, which keeps it from reading as a flat stripe
  const hazeR = box.w * 0.5;
  ctx.save();
  ctx.translate( cx, hy );
  ctx.scale( 1, box.h * 0.09 / hazeR );
  const band = ctx.createRadialGradient( 0, 0, 0, 0, 0, hazeR );
  for ( let i = 0; i <= 10; i++ ) {
    const t = i / 10;
    band.addColorStop( t, rgba( floorAt( 0 ), 0.26 * Math.pow( 1 - t, 2.2 ) ) );
  }
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = band;
  ctx.fillRect( -hazeR, -hazeR, hazeR * 2, hazeR * 2 );
  ctx.restore();
  neon( tube( sunBottom ), 2.4, () => {
    ctx.moveTo( 0, hy );
    ctx.lineTo( w, hy );
  } );
}
